import React from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  BookOpen,
  Drama,
  Lock,
  Coins,
  Clapperboard,
  Gift,
  Trophy,
  DoorOpen,
  Eye,
  Link2,
  Banknote,
  Camera,
  Brain,
  ChevronDown,
} from "lucide-react";
import Atmosphere from "@/components/game/Atmosphere";
import AnnouncementBar from "@/components/game/AnnouncementBar";
import FallingCard from "@/components/game/FallingCard";
import GlassPanel from "@/components/game/GlassPanel";
import GuardCardsCarousel from "@/components/game/GuardCardsCarousel";
import ImageGallery from "@/components/game/ImageGallery";

const ease = [0.2, 0.8, 0.2, 1];

// Shopify Starter Kit product page — all purchase CTAs point here (same tab)
const SHOPIFY_URL =
  "https://invisibleplayerbook.myshopify.com/products/the-invisible-player-how-reading-bodies-and-minds-gave-me-an-unfair-advantage";
const ENTER_THE_GAME_URL = SHOPIFY_URL;
const TELEGRAM_URL = SHOPIFY_URL;

// Giveaway links — leave empty until the Giveaway landing page & rules are ready
const GIVEAWAY_URL = "https://monumental-souffle-f0846d.netlify.app";
const OFFICIAL_RULES_URL = "";

const STATS = [
  { n: "100+", label: "Practical Reads & Signals", desc: "Body-language observations and practical behavioral reads from feet to head." },
  { n: "40", label: "Signal Combinations", desc: "Advanced combinations of physical signals + response time." },
  { n: "35", label: "Micro & Macro Expressions", desc: "Poker-specific micro and macro expressions to observe at the table." },
  { n: "35", label: "Self-Touch Micro-Gestures", desc: "Detailed readings of gestures involving the face, head, neck, and body." },
  { n: "25", label: "Psychological Questions", desc: "Questions designed to provoke reactions and help expose behavioral changes at the poker table." },
  { n: "15", label: "All-In Signals", desc: "Physical and behavioral signals to observe during high-pressure all-in situations." },
  { n: "♠", label: "Body Language: Feet to Head", desc: "Detailed sections covering feet, legs, hips, abdomen, chest, shoulders, arms, hands, fingers, neck, and head." },
  { n: "♠", label: "Poker Psychology & Reading Systems", desc: "Bluff vs. strong-hand radar, response-time analysis, observation sequences, player profiles, table presence, mental mistakes, and practical poker reads." },
];

const SectionBg = ({ img }) => (
  <div className="section-bg" style={{ backgroundImage: `url(/bg/${img})` }} aria-hidden />
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.5 }}
    transition={{ duration: 0.7, delay, ease }}
  >
    {children}
  </motion.div>
);

const Bullet = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 shrink-0 w-8 h-8 rounded-lg border border-gold/30 bg-gold/5 flex items-center justify-center text-gold">
      <Icon size={16} strokeWidth={1.6} />
    </span>
    <span className="text-[#c8c2b5] leading-relaxed text-[0.95rem]">{children}</span>
  </li>
);

const H2 = ({ children }) => (
  <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] gold-gradient tracking-wide">
    {children}
  </h2>
);

export default function InvisibleGame() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

  return (
    <main className="relative felt-bg text-[#e9e6df] overflow-clip" data-testid="invisible-game">
      <AnnouncementBar />
      <motion.div className="scroll-rail" style={{ scaleX }} data-testid="scroll-progress" />
      <Atmosphere />
      <FallingCard />

      {/* ================= SECTION 1 — HERO ================= */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 z-10 lg:items-start lg:text-left lg:px-[7vw]"
        data-testid="section-hero"
      >
        <SectionBg img="hero.webp" />
        <div className="rim-light" />
        <Reveal className="relative z-10">
          <span className="eyebrow">The Invisible Player</span>
        </Reveal>
        <motion.h1
          className="relative z-10 font-display mt-6 text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl gold-gradient lg:mx-0"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
        >
          THEY PLAY CARDS.
          <br /> YOU PLAY PEOPLE.
        </motion.h1>
        <motion.p
          className="relative z-10 mt-7 max-w-xl text-base sm:text-lg text-[#b0aa9c] lg:mx-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.35, ease }}
        >
          Welcome to the table where nothing is what it seems. Scroll to take your seat.
        </motion.p>
        <motion.div
          className="relative z-10 mt-14 flex flex-col items-center lg:items-start text-gold/80"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
        >
          <span className="text-xs tracking-[0.35em] uppercase">Enter the game</span>
          <ChevronDown className="bob mt-2" size={22} />
        </motion.div>
      </section>

      {/* ================= SECTION 2 — THE BOOK / STARTER KIT (RIGHT) ================= */}
      <section id="starter-kit" className="relative min-h-screen flex items-center px-6 sm:px-10 z-10" data-testid="section-book">
        <SectionBg img="book.webp" />
        <div className="w-full max-w-6xl mx-auto relative z-10">
          <GlassPanel side="right" testId="panel-book" className="p-8 sm:p-12 max-w-xl">
            <span className="eyebrow">The Starter Kit</span>
            <H2>READ THE BOOK. THEN READ EVERYONE ELSE.</H2>
            <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
              The Invisible Player goes far beyond poker tells. Across 400+ pages, you'll explore
              body language, micro-expressions, psychology, poker strategy, decision-making,
              observation, gratitude, spirituality and the inner game behind every player.
            </p>
            <p className="mt-4 text-[#c0baad] leading-relaxed text-[0.95rem]">
              Learn the game. Read the room. Understand people. And somewhere along the way,
              understand yourself.
            </p>
            <p className="mt-5 text-sm text-gold/70 uppercase tracking-widest">
              Everything you need to enter the game
            </p>
            <ul className="mt-4 space-y-3">
              <Bullet icon={BookOpen}>The Invisible Player (full book)</Bullet>
              <Bullet icon={Drama}>Your Guard Card — pick one from our collection</Bullet>
              <Bullet icon={Lock}>Private community: exclusive scene analyses &amp; techniques</Bullet>
              <Bullet icon={Coins}>
                Access to community projects — products you can sell, commissions you keep
              </Bullet>
            </ul>
            <p className="mt-6 italic text-[#9c968a]">
              We don't just sharpen your mind. We grow your balance too ;)
            </p>
            <ImageGallery
              images={[
                "/kit/kit1.webp",
                "/kit/kit2.webp",
                "/kit/kit3.webp",
                "/kit/kit4.webp",
                "/kit/kit5.webp",
                "/kit/kit6.webp",
              ]}
              label={null}
            />
            <div className="mt-7 flex items-center gap-3 flex-wrap" data-testid="starter-kit-price">
              <span className="line-through text-[#8a857a] text-lg">$68.90</span>
              <span className="font-display text-gold text-3xl">$49.90</span>
              <span className="text-[10px] tracking-[0.18em] uppercase text-gold border border-gold/40 rounded-full px-3 py-1 bg-gold/5">
                First 100 Members
              </span>
            </div>
            <a
              href={ENTER_THE_GAME_URL}
              target="_top"
              rel="noopener"
              data-testid="cta-book"
              className="btn-gold mt-5"
            >
              ENTER THE GAME — $49.90
            </a>
          </GlassPanel>
        </div>
      </section>

      {/* ================= WHAT'S INSIDE THE INVISIBLE PLAYER ================= */}
      <section
        className="relative min-h-screen flex items-center px-6 sm:px-10 z-50 py-24"
        data-testid="section-whats-inside"
      >
        <div
          className="absolute inset-0 z-0"
          aria-hidden
          style={{
            background:
              "radial-gradient(70% 50% at 50% 10%, rgba(212,175,55,0.10), rgba(10,10,10,0) 65%), #0a0a0a",
          }}
        />
        <div className="relative z-50 w-full max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <Reveal>
              <span className="eyebrow">Inside the mind</span>
            </Reveal>
            <Reveal delay={0.08}>
              <H2>WHAT'S INSIDE THE INVISIBLE PLAYER</H2>
            </Reveal>
            <Reveal delay={0.11}>
              <p
                className="mt-5 font-display text-[0.95rem] leading-relaxed gold-gradient"
                data-testid="whats-inside-caption"
              >
                THE INVISIBLE PLAYER WAS CREATED TO AWAKEN A VISION BEYOND THE GAME — GRATITUDE,
                SPIRITUALITY, THE ART OF READING BODIES AND MINDS. LEARN POKER. ENTER OUR UNIVERSE
                OF PLAYERS.
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
                Not theory for theory's sake. A field guide built to sharpen how you observe,
                think, decide and play.
              </p>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={Math.min(i * 0.05, 0.35)}>
                <div className="h-full rounded-xl border border-gold/20 bg-black/40 p-5 hover:border-gold/45 transition-colors">
                  <div className="font-display text-3xl sm:text-4xl gold-gradient leading-none">
                    {s.n}
                  </div>
                  <div className="mt-3 text-[11px] sm:text-xs tracking-[0.14em] uppercase text-gold/80">
                    {s.label}
                  </div>
                  <p className="mt-2 text-[13px] text-[#9c968a] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 text-center max-w-2xl mx-auto">
            <Reveal>
              <h3 className="font-display text-2xl sm:text-3xl gold-gradient tracking-wide">
                AND THAT'S ONLY PART OF THE BOOK.
              </h3>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
                Positions. Ranges. Environment reading. Cold reading. Emotional control.
                Decision-making. Gratitude. Spirituality. Observation. The inner game.
              </p>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-6 italic text-gold-soft/90 font-display text-lg leading-relaxed">
                One book. Hundreds of signals. A different way to see the table — and the people
                around it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative min-h-screen flex items-center px-6 sm:px-10 z-10" data-testid="section-community">
        <SectionBg img="community.webp" />
        <div className="w-full max-w-6xl mx-auto relative z-10">
          <GlassPanel side="left" testId="panel-community" className="p-8 sm:p-12 max-w-xl">
            <span className="eyebrow">The Community</span>
            <H2>THE TABLE IS FULL OF PLAYERS. FEW ARE INVISIBLE.</H2>
            <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
              Inside, members get weekly scene breakdowns — real poker hands, real interrogations,
              real negotiations — decoded gesture by gesture. Plus free giveaways of exclusive
              printed pieces, member-only drops, and first access to everything we build.
            </p>
            <ul className="mt-6 space-y-3">
              <Bullet icon={Clapperboard}>Weekly analysis: poker scenes &amp; body language, decoded</Bullet>
              <Bullet icon={Gift}>Free giveaways — exclusive Guard Cards, printed for members</Bullet>
              <Bullet icon={Trophy}>Member rankings &amp; rewards</Bullet>
              <Bullet icon={DoorOpen}>Early access to new products and projects</Bullet>
            </ul>
            <a
              href={TELEGRAM_URL}
              target="_top"
              rel="noopener"
              data-testid="cta-community"
              className="btn-gold mt-7"
            >
              JOIN THE INVISIBLE
            </a>
          </GlassPanel>
        </div>
      </section>

      {/* ================= SECTION 4 — THE APPS (RIGHT) ================= */}
      <section className="relative min-h-screen flex items-center px-6 sm:px-10 z-10" data-testid="section-apps">
        <SectionBg img="apps.webp" />
        <div className="w-full max-w-6xl mx-auto relative z-10">
          <GlassPanel side="right" testId="panel-apps" className="p-8 sm:p-12 max-w-xl">
            <span className="eyebrow">Tools</span>
            <H2>TOOLS FOR THE OBSERVANT.</H2>
            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gold/20 bg-black/30 p-5">
                <Camera className="text-gold mb-3" size={22} strokeWidth={1.5} />
                <h4 className="font-display text-lg text-[#f3ecd9]">Invisible Reader</h4>
                <p className="text-sm text-[#9c968a] mt-2 leading-relaxed">
                  Photograph any page. Listen to it anywhere. Your books, unlocked.
                </p>
              </div>
              <div className="rounded-xl border border-gold/20 bg-black/30 p-5">
                <Brain className="text-gold mb-3" size={22} strokeWidth={1.5} />
                <h4 className="font-display text-lg text-[#f3ecd9]">Invisible Companion</h4>
                <p className="text-sm text-[#9c968a] mt-2 leading-relaxed">
                  Train your observation daily. Read others. Read yourself. Six layers of mind.
                </p>
              </div>
            </div>
            <span className="inline-block mt-6 text-xs tracking-[0.3em] uppercase text-gold/70 border border-gold/30 rounded-full px-4 py-2">
              Coming soon — members get first access
            </span>
          </GlassPanel>
        </div>
      </section>

      {/* ================= SECTION 5 — GUARD CARDS CATALOG (CENTER WIDE) ================= */}
      <section className="relative min-h-screen flex items-center px-6 sm:px-10 z-10" data-testid="section-guards">
        <SectionBg img="guards.webp" />
        <div className="w-full max-w-6xl mx-auto relative z-10">
          <GlassPanel side="center" impactCorner="top-center" testId="panel-guards" className="p-8 sm:p-12 w-full">
            <div className="text-center max-w-2xl mx-auto">
              <span className="eyebrow">Guard Cards</span>
              <H2>EVERY PLAYER NEEDS A GUARDIAN.</H2>
              <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
                Hand-finished and printed one by one in Massachusetts. Each Card Guard is crafted
                to turn your table presence into a statement. Your cards deserve a guardian.
              </p>
            </div>
            <div className="mt-10">
              <div className="text-center max-w-2xl mx-auto mb-8">
                <h3 className="font-display text-2xl sm:text-3xl gold-gradient tracking-wide">
                  SAY IT WITHOUT SAYING IT.
                </h3>
                <p className="mt-3 text-[#c0baad] text-[0.95rem] leading-relaxed">
                  Every guard is a message. Your opponents will feel it — but never hear it.
                </p>
              </div>
              <GuardCardsCarousel />
            </div>
          </GlassPanel>
        </div>
      </section>

      {/* ================= SECTION 6 — INVISIBLE PLAYERS PROGRAM + FINAL ================= */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 sm:px-10 z-10 pt-24 pb-16" data-testid="section-program">
        <SectionBg img="program.webp" />
        <div className="w-full max-w-6xl mx-auto relative z-10">
          <GlassPanel side="center" impactCorner="top-center" testId="panel-program" className="p-8 sm:p-14 w-full">
            <div className="max-w-3xl mx-auto text-center">
              <span className="eyebrow">Invisible Players Program</span>
              <H2>STOP WATCHING THE GAME. START PROFITING FROM IT.</H2>
              <p className="mt-5 text-[#c0baad] leading-relaxed text-[0.95rem]">
                Members get access to our product catalog. Pick a product and start selling. Every
                sale pays you a commission. No stock. No shipping. No risk. We print, we ship — you
                get paid.
              </p>
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              {[
                { icon: Eye, n: "01", t: "Browse the community catalog" },
                { icon: Link2, n: "02", t: 'Pick your product and grab your Starter Kit' },
                { icon: Banknote, n: "03", t: "Share it. Earn on every sale." },
              ].map((s) => (
                <div key={s.n} className="rounded-xl border border-gold/20 bg-black/30 p-6 text-center">
                  <s.icon className="text-gold mx-auto" size={26} strokeWidth={1.5} />
                  <p className="font-display text-gold/50 mt-3">{s.n}</p>
                  <p className="text-sm text-[#c8c2b5] mt-2 leading-relaxed">{s.t}</p>
                </div>
              ))}
            </div>
            <p className="text-center mt-8 italic text-gold-soft/90 text-lg font-display">
              Your network is your bankroll.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href={SHOPIFY_URL}
                target="_top"
                rel="noopener"
                data-testid="sell-this-cta"
                className="btn-gold"
              >
                YOU WANNA SELL THIS?
              </a>
            </div>
          </GlassPanel>
        </div>

        {/* Landing table */}
        <div className="relative z-10 w-full max-w-3xl mx-auto mt-24 text-center">
          <div className="hairline max-w-xs mx-auto" />
        </div>
      </section>

      {/* ================= iPHONE GIVEAWAY (bonus/opportunity — moved lower) ================= */}
      <section
        className="relative min-h-screen flex items-center justify-center px-6 sm:px-10 z-10 overflow-hidden"
        data-testid="section-giveaway"
      >
        <div
          className="absolute inset-0 z-0"
          aria-hidden
          style={{
            background:
              "radial-gradient(120% 90% at 50% 30%, rgba(20,42,30,0.7) 0%, rgba(10,10,10,0) 60%), radial-gradient(60% 45% at 50% 42%, rgba(212,175,55,0.16), rgba(10,10,10,0) 70%), #0a0a0a",
          }}
        />
        <div className="relative z-10 w-full max-w-3xl mx-auto">
          <GlassPanel side="center" impactCorner="top-center" testId="panel-giveaway" className="p-9 sm:p-16 w-full">
            <div className="text-center">
              <Reveal>
                <span className="eyebrow">The Invisible Players Giveaway</span>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="font-display mt-5 leading-[1.04]">
                  <span className="block text-2xl sm:text-3xl tracking-[0.14em] text-[#e9e6df]">
                    WIN AN
                  </span>
                  <span className="block text-4xl sm:text-6xl gold-gradient mt-2">
                    iPHONE 17 PRO MAX
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-7 max-w-xl mx-auto text-[#c0baad] leading-relaxed text-[0.95rem]">
                  Here, you don't just upgrade your mind. You enter a community built around
                  observation, strategy and opportunity — with exclusive giveaways for Invisible
                  Players.
                </p>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="mt-9 flex justify-center">
                  <a
                    href={GIVEAWAY_URL || undefined}
                    target="_top"
                    rel="noopener"
                    data-testid="giveaway-cta"
                    className="btn-gold"
                  >
                    I WANT TO PARTICIPATE
                  </a>
                </div>
                <p className="mt-6 text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-[#8a857a] leading-relaxed">
                  No purchase necessary. A purchase will not increase your chances of winning.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (OFFICIAL_RULES_URL) window.location.href = OFFICIAL_RULES_URL;
                  }}
                  data-testid="giveaway-rules"
                  className="mt-3 text-[11px] tracking-[0.2em] uppercase text-gold/70 hover:text-gold underline underline-offset-4 transition-colors"
                >
                  Official Rules
                </button>
              </Reveal>
            </div>
          </GlassPanel>
        </div>
      </section>

      {/* ================= FINAL CLOSING CTA ================= */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center px-6 sm:px-10 z-50 pt-24 pb-16"
        data-testid="section-final"
      >
        <SectionBg img="program.webp" />
        <div className="relative z-10 w-full max-w-3xl mx-auto text-center" data-testid="final-cta">
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-10 w-[90%] h-40 rounded-[50%]"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212,175,55,0.18), rgba(0,0,0,0) 70%)",
              filter: "blur(8px)",
            }}
          />
          <Reveal>
            <span className="eyebrow">Your seat is waiting</span>
          </Reveal>
          <Reveal delay={0.08}>
            <h3 className="font-display text-3xl sm:text-5xl gold-gradient mt-4 leading-[1.08]">
              YOU'VE SEEN WHAT'S INSIDE. NOW ENTER THE GAME.
            </h3>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 max-w-xl mx-auto text-[#c0baad] leading-relaxed text-[0.95rem]">
              The book. Your Card Guard. The community. One entry point into the Invisible Player
              universe.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-7 flex items-center justify-center gap-3 flex-wrap" data-testid="final-price">
              <span className="line-through text-[#8a857a] text-lg">$68.90</span>
              <span className="font-display text-gold text-3xl">$49.90</span>
              <span className="text-[10px] tracking-[0.18em] uppercase text-gold border border-gold/40 rounded-full px-3 py-1 bg-gold/5">
                First 100 Members
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={ENTER_THE_GAME_URL}
                target="_top"
                rel="noopener"
                data-testid="final-cta-book"
                className="btn-gold"
              >
                ENTER THE GAME — $49.90
              </a>
              <a
                href={TELEGRAM_URL}
                target="_top"
                rel="noopener"
                data-testid="final-cta-member"
                className="btn-ghost"
              >
                BECOME AN INVISIBLE PLAYER
              </a>
            </div>
          </Reveal>
          <div className="hairline mt-16 max-w-xs mx-auto" />
          <p className="mt-6 text-xs tracking-[0.3em] uppercase text-[#6b665c]">
            The Invisible Player — read the game
          </p>
        </div>
      </section>
    </main>
  );
}
